import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search, Loader2, Bookmark, BookmarkCheck, X, UserCheck,
} from 'lucide-react';
import { useAthleteBookmark } from '@/hooks/useAthleteBookmark';
import { useAuth } from '@/contexts/SupabaseAuthContext';

/**
 * Dialog for bookmarking/unbookmarking an exercise on behalf of an athlete.
 *
 * Step 1: pick an athlete (search for admins, assigned list for support).
 * Step 2: toggle the bookmark for the selected exercise.
 */
const BookmarkForAthleteDialog = ({ exerciseId, exerciseName, open, onOpenChange }) => {
  const { isAdmin, isSupport } = useAuth();
  const {
    athletes,
    athleteSearchLoading,
    searchAthletes,
    isAthleteBookmarked,
    bookmarksLoading,
    fetchAthleteBookmarks,
    addBookmark,
    removeBookmark,
    busy,
  } = useAthleteBookmark();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAthlete, setSelectedAthlete] = useState(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedAthlete(null);
    } else if (isSupport) {
      searchAthletes('');
    }
  }, [open, isSupport, searchAthletes]);

  // Debounced athlete search
  useEffect(() => {
    if (!open || selectedAthlete) return;
    const timer = setTimeout(() => {
      searchAthletes(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, open, selectedAthlete, searchAthletes]);

  // Fetch athlete's bookmarks when selected
  useEffect(() => {
    if (selectedAthlete) {
      fetchAthleteBookmarks(selectedAthlete.id);
    }
  }, [selectedAthlete, fetchAthleteBookmarks]);

  const handleSelectAthlete = useCallback((athlete) => {
    setSelectedAthlete(athlete);
    setSearchTerm('');
  }, []);

  const handleClearAthlete = useCallback(() => {
    setSelectedAthlete(null);
  }, []);

  const handleToggle = useCallback(async () => {
    if (!selectedAthlete || !exerciseId) return;
    if (isAthleteBookmarked(exerciseId)) {
      await removeBookmark(selectedAthlete.id, exerciseId);
    } else {
      await addBookmark(selectedAthlete.id, exerciseId);
    }
  }, [selectedAthlete, exerciseId, isAthleteBookmarked, addBookmark, removeBookmark]);

  const athleteName = selectedAthlete?.full_name || selectedAthlete?.email || 'athlete';
  const bookmarked = isAthleteBookmarked(exerciseId);
  const minChars = isAdmin ? 2 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            Bookmark for Athlete
          </DialogTitle>
        </DialogHeader>

        {exerciseName && (
          <p className="text-sm text-muted-foreground -mt-2 truncate">
            {exerciseName}
          </p>
        )}

        <div className="space-y-4">
          {/* Athlete selection */}
          {selectedAthlete ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2.5">
              <UserCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium truncate block">
                  {selectedAthlete.full_name || 'No name'}
                </span>
                {selectedAthlete.email && (
                  <span className="text-xs text-muted-foreground truncate block">
                    {selectedAthlete.email}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0"
                onClick={handleClearAthlete}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={
                    isAdmin
                      ? 'Search athletes by name or email…'
                      : 'Search assigned athletes…'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>

              <div className="max-h-60 overflow-y-auto space-y-1">
                {athleteSearchLoading ? (
                  <div className="space-y-2 p-1">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-11 w-full rounded-md" />
                    ))}
                  </div>
                ) : athletes.length > 0 ? (
                  athletes.map((athlete) => (
                    <button
                      key={athlete.id}
                      type="button"
                      className="w-full flex items-center gap-3 rounded-md p-2 text-left hover:bg-muted/50 transition-colors"
                      onClick={() => handleSelectAthlete(athlete)}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">
                          {athlete.full_name || 'No name'}
                        </span>
                        {athlete.email && (
                          <span className="text-xs text-muted-foreground truncate block">
                            {athlete.email}
                          </span>
                        )}
                      </div>
                      {athlete.total_sessions > 0 && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">
                          {athlete.total_sessions} sessions
                        </Badge>
                      )}
                    </button>
                  ))
                ) : searchTerm.trim().length >= minChars ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No athletes found.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    {isAdmin
                      ? 'Type at least 2 characters to search.'
                      : 'No assigned athletes.'}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Bookmark toggle for selected athlete */}
          {selectedAthlete && (
            <div className="pt-1">
              {bookmarksLoading ? (
                <Skeleton className="h-9 w-full rounded-md" />
              ) : (
                <Button
                  className="w-full gap-2"
                  variant={bookmarked ? 'destructive' : 'default'}
                  disabled={busy}
                  onClick={handleToggle}
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : bookmarked ? (
                    <BookmarkCheck className="h-4 w-4" />
                  ) : (
                    <Bookmark className="h-4 w-4" />
                  )}
                  {bookmarked
                    ? `Remove bookmark for ${athleteName}`
                    : `Bookmark for ${athleteName}`}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookmarkForAthleteDialog;
